/**
 * Passwordless sign-in with an emailed code, shared by BOTH clients.
 *
 * There is no "use a code instead" control: the ONE Continue button on the
 * sign-in card does both jobs, and which one is decided by the password field
 * alone — typed → password sign-in-or-sign-up (`emailContinue`), left empty →
 * a 6-digit code is emailed and typing it signs the user in. The button's
 * label says which ("Continue" / "Email me a code") so nothing is sent by
 * surprise. Both platforms read this ONE predicate so they cannot disagree.
 *
 * Why the code path exists at all: users on a device without their password
 * manager, users who never set a password (a code-only sign-up creates the
 * account with none — the worker's `emailOTP` plugin allows sign-up on a
 * sign-in code, and Settings → Security offers "Set password" afterwards), and
 * the `<mailbox>+flashka_test@<domain>` accounts every dev/preview environment
 * signs in with, whose code is fixed and never emailed while the worker's test
 * mode is armed. Any domain works; what the first sign-up needs is a mailbox
 * that has no account of its own in that environment (the worker's subaddress
 * guard refuses `davide+flashka_test@x` when `davide@x` is already a user).
 *
 * The half that is easy to get wrong, kept here so it is written once: the
 * code must be requested as type `sign-in` — `email-verification` is the
 * SIGN-UP path and its code will not sign anyone in. Callers on both
 * platforms must still drop any previous user's cached credentials BEFORE
 * `signInWithCode`, because it mints a session (web: `resetLocalCredentials`,
 * mobile: `clearJwtCache`).
 *
 * Per this package's rule (see ./index.ts) nothing here imports better-auth:
 * the client arrives as a structural slice both apps' clients satisfy.
 */
import type { AuthErrorLike } from './auth-error';

export type ContinueMode = 'password' | 'code';

/**
 * What the sign-in card's Continue button does with the current fields. The
 * password is the ONLY input: an email is required either way and is
 * validated by the field itself.
 */
export function continueMode(password: string): ContinueMode {
  return password.length > 0 ? 'password' : 'code';
}

/**
 * Whether the sign-in card offers "Forgot password?" at all.
 *
 * Hidden until an email has been typed (Simo, 2026-09-08). The link is
 * primary-coloured, so on the resting card it was the loudest thing under the
 * fields — and pressing it could not do anything, because the reset code has
 * nowhere to send to without an address. Absent, not disabled: a control that
 * is only ever there when it works needs no explanation.
 *
 * Both platforms read THIS rather than an inline `!!email`, so they cannot
 * disagree about when the link appears — nor about whitespace, which is not an
 * address.
 */
export function forgotPasswordAvailable(email: string): boolean {
  return email.trim().length > 0;
}

type AuthResult = { error?: AuthErrorLike };

/** The slice of a Better Auth client this flow drives (structural on purpose). */
export interface EmailCodeSignInClient {
  emailOtp: {
    sendVerificationOtp(input: { email: string; type: 'sign-in' }): Promise<AuthResult>;
  };
  signIn: {
    emailOtp(input: { email: string; otp: string }): Promise<AuthResult>;
  };
}

/** Emails a sign-in code. Resolves with `error` unset when the code is on its way. */
export function requestSignInCode(
  client: EmailCodeSignInClient,
  email: string,
): Promise<AuthResult> {
  return client.emailOtp.sendVerificationOtp({ email, type: 'sign-in' });
}

/**
 * Exchanges the emailed code for a session. Creates the account when the email
 * has none — the same "sign in or sign up" promise the card's title makes.
 */
export function signInWithCode(
  client: EmailCodeSignInClient,
  email: string,
  otp: string,
): Promise<AuthResult> {
  return client.signIn.emailOtp({ email, otp });
}
